import React, { useState } from 'react';
import { Form, Button, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { StepComponentProps } from './OrganizationWizard';
import { employeeRecordApi } from '../api/employeeRecordValueApi';
import { useToasts } from 'common/toast';

type RecordField = {
  tempId: string;
  id?: number; // server id when saved
  name: string;
  type_of_value: number;
};

const TYPE_OPTIONS = [
  { key: 2, value: 2, text: 'Number' },
];

const OrganizationWizardStep7MemberRecords: React.FC<StepComponentProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
  onCancel,
  isLastStep,
}) => {
  const { addToast } = useToasts();
  const [records, setRecords] = useState<RecordField[]>(data.memberRecords || []);
  const [editingRecord, setEditingRecord] = useState<RecordField | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [createFieldType] = employeeRecordApi.endpoints.apiAdminV1EmployeeRecordValuesCreate.useMutation();
  const [updateFieldType] = employeeRecordApi.endpoints.apiAdminV1EmployeeRecordValuesUpdate.useMutation();
  const [deleteFieldType] = employeeRecordApi.endpoints.apiAdminV1EmployeeRecordValuesDestroy.useMutation();

  const handleAddRecord = () => {
    const newRecord: RecordField = {
      tempId: `temp-${Date.now()}`,
      name: '',
      type_of_value: 2,
    };
    const updated = [...records, newRecord];
    setRecords(updated);
    setEditingRecord(newRecord);
    onDataChange({ memberRecords: updated });
  };

  const handleUpdateRecord = (tempId: string, updates: Partial<RecordField>) => {
    const updated = records.map(rec => (rec.tempId === tempId ? { ...rec, ...updates } : rec));
    setRecords(updated);
    if (editingRecord?.tempId === tempId) {
      setEditingRecord({ ...editingRecord, ...updates });
    }
    onDataChange({ memberRecords: updated });
  };

  const handleDeleteRecord = async (tempId: string) => {
    const rec = records.find(r => r.tempId === tempId);
    if (rec?.id) {
      try {
        await deleteFieldType({ id: rec.id }).unwrap();
        addToast('Member record deleted', { appearance: 'success' });
      } catch (err) {
        console.error('Failed to delete member record:', err);
        addToast('Failed to delete record', { appearance: 'error' });
      }
    }

    const updated = records.filter(rec => rec.tempId !== tempId);
    setRecords(updated);
    if (editingRecord?.tempId === tempId) {
      setEditingRecord(null);
    }
    onDataChange({ memberRecords: updated });
  };

  const hasUnnamedRecords = records.some(record => !record.name.trim());

  const handleComplete = async () => {
    if (!data.organizationId) {
      addToast('Organization must be created before adding member records', { appearance: 'error' });
      return;
    }

    setIsSaving(true);
    const updatedRecords: RecordField[] = [];
    let createdCount = 0;
    let updatedCount = 0;

    try {
      for (const rec of records) {
        const payload: any = {
          name: rec.name,
          organization: data.organizationId,
          type_of_value: rec.type_of_value,
        };

        if (rec.id) {
          const result = await updateFieldType({ id: rec.id, employeeRecordValueCreateUpdate: payload }).unwrap();
          updatedRecords.push({ ...rec, id: result.id });
          updatedCount++;
        } else {
          const result = await createFieldType({ employeeRecordValueCreateUpdate: payload }).unwrap();
          updatedRecords.push({ ...rec, id: result.id });
          createdCount++;
        }
      }

      if (createdCount > 0) addToast(`${createdCount} ${createdCount === 1 ? 'record' : 'records'} created`, { appearance: 'success' });
      if (updatedCount > 0) addToast(`${updatedCount} ${updatedCount === 1 ? 'record' : 'records'} updated`, { appearance: 'success' });

      setRecords(updatedRecords);
      onDataChange({ memberRecords: updatedRecords });
      onNext();
    } catch (err) {
      console.error('Failed to save member records:', err);
      addToast('Failed to save member records', { appearance: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <WizardStepContainer>
      <SectionTitle>Member Records</SectionTitle>
      <HelpText>
        Create custom fields to track member-level data. These are optional and can be added later.
      </HelpText>

      <ContentSection>
        <LeftPanel>
          <PanelHeader>
            <PanelTitle>Records ({records.length})</PanelTitle>
            <AddButton onClick={handleAddRecord} type="button">
              <Icon name="plus" /> Add Record
            </AddButton>
          </PanelHeader>

          {records.length === 0 ? (
            <EmptyState>
              <Icon name="user" size="huge" />
              <EmptyText>No records created yet</EmptyText>
              <EmptySubtext>Click "Add Record" to create a new member record</EmptySubtext>
            </EmptyState>
          ) : (
            <RecordList>
              {records.map(record => (
                <RecordItem
                  key={record.tempId}
                  $active={editingRecord?.tempId === record.tempId}
                  onClick={() => setEditingRecord(record)}
                >
                  <RecordName>
                    {record.name || '(Unnamed Record)'}
                  </RecordName>
                  <RecordMeta>
                    {TYPE_OPTIONS.find(t => t.value === record.type_of_value)?.text || 'Number'}
                  </RecordMeta>
                  <DeleteButton
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteRecord(record.tempId);
                    }}
                    type="button"
                  >
                    <Icon name="trash" />
                  </DeleteButton>
                </RecordItem>
              ))}
            </RecordList>
          )}
        </LeftPanel>

        <RightPanel>
          {editingRecord ? (
            <>
              <PanelHeader>
                <PanelTitle>Configure Record</PanelTitle>
              </PanelHeader>

              <FormContent>
                <Form>
                  <Form.Field required>
                    <label>Field Name</label>
                    <Form.Input
                      placeholder="e.g., Years of Service"
                      value={editingRecord.name}
                      onChange={(_, { value }) =>
                        handleUpdateRecord(editingRecord.tempId, { name: value })
                      }
                    />
                  </Form.Field>
                </Form>
              </FormContent>
            </>
          ) : (
            <EmptyState>
              <Icon name="pencil" size="huge" />
              <EmptyText>No record selected</EmptyText>
              <EmptySubtext>Select a record from the list to configure it</EmptySubtext>
            </EmptyState>
          )}
        </RightPanel>
      </ContentSection>

      <ButtonContainer>
        <LeftButtons>
          <Button onClick={onBack}>
            Back
          </Button>
          <CancelButton onClick={onCancel}>
            Cancel Setup
          </CancelButton>
        </LeftButtons>
        <CompleteButton onClick={handleComplete} disabled={hasUnnamedRecords || isSaving}>
          {isSaving ? 'Saving...' : 'Complete Setup'}
        </CompleteButton>
      </ButtonContainer>
    </WizardStepContainer>
  );
};

export default OrganizationWizardStep7MemberRecords;

const WizardStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
    padding-right: 32px;

`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #666;
  margin: -8px 0 0 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 20px;
  min-height: 200px;
`;

const LeftPanel = styled.div`
  flex: 0 0 300px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPanel = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const FormContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const PanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  background: #2185d0;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1678c2;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const RecordList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const RecordItem = styled.div<{ $active: boolean }>`
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => (props.$active ? '#e3f2fd' : 'white')};
  position: relative;

  &:hover {
    background: ${props => (props.$active ? '#e3f2fd' : '#f5f5f5')};
  }
`;

const RecordName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const RecordMeta = styled.div`
  font-size: 11px;
  color: #888;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: #db2828;
  cursor: pointer;
  padding: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }

  i.icon {
    margin: 0 !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  text-align: center;
  height: 100%;

  i.icon {
    color: #ccc;
    margin-bottom: 16px;
  }
`;

const EmptyText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
`;

const EmptySubtext = styled.div`
  font-size: 13px;
  color: #999;
`;

const CompletionInfo = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;

  i.icon {
    color: #2185d0;
    font-size: 24px;
    margin: 0 !important;
  }
`;

const CompletionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const CompletionText = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const CompleteButton = styled.button`
  padding: 10px 32px;
  background: #21ba45;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(33, 186, 69, 0.3);

  &:hover {
    background: #16ab39;
    box-shadow: 0 3px 6px rgba(33, 186, 69, 0.4);
  }
`;

